/**
 * End-to-end encryption of relayed console traffic (ORGU-283, ADR 0042 layer 2).
 *
 * The browser and the hub run Noise_NK_25519_AESGCM_SHA256: the browser (initiator) already holds
 * the hub's static X25519 public key, the one registered for the org, and the handshake only
 * completes against a hub that holds the matching private key. The relay (the control plane's
 * Durable Object) forwards the resulting frames as opaque text and can neither read nor change
 * them: a changed frame fails AES-GCM on the receiving side and the session reports a decryption
 * error and stops (no further frame is accepted after one fails).
 *
 * WebCrypto only (X25519, AES-GCM, HMAC-SHA-256), so the same code runs in the browser, on Node
 * and in workerd. Frames are `n1.` + base64url, so a text WebSocket carries them unchanged.
 */

const PROTOCOL_NAME = 'Noise_NK_25519_AESGCM_SHA256';
const FRAME_PREFIX = 'n1.';
const DH_LEN = 32;
const TAG_LEN = 16;
/** Noise's limit on one message; the relay refuses anything longer. */
export const RELAY_FRAME_MAX_BYTES = 65_535;
const MAX_NONCE = 2 ** 53 - 1;

export type RelayCryptoErrorCode = 'decrypt' | 'hub-key-mismatch' | 'bad-frame' | 'closed';

export class RelayCryptoError extends Error {
  readonly code: RelayCryptoErrorCode;
  constructor(code: RelayCryptoErrorCode, message: string) {
    super(message);
    this.name = 'RelayCryptoError';
    this.code = code;
  }
}

/*
 * The package compiles with no DOM or Node types (it is shared by every runtime), so the few
 * WebCrypto and text members used here are typed structurally.
 */
type CryptoKey = { readonly type: string };
type CryptoKeyPair = { publicKey: CryptoKey; privateKey: CryptoKey };
type Algorithm = string | Record<string, unknown>;
interface SubtleCrypto {
  digest(alg: Algorithm, data: ArrayBuffer): Promise<ArrayBuffer>;
  importKey(format: 'raw', key: ArrayBuffer, alg: Algorithm, extractable: boolean, usages: string[]): Promise<CryptoKey>;
  exportKey(format: 'raw', key: CryptoKey): Promise<ArrayBuffer>;
  generateKey(alg: Algorithm, extractable: boolean, usages: string[]): Promise<CryptoKeyPair>;
  deriveBits(alg: Algorithm, key: CryptoKey, length: number): Promise<ArrayBuffer>;
  sign(alg: Algorithm, key: CryptoKey, data: ArrayBuffer): Promise<ArrayBuffer>;
  encrypt(alg: Algorithm, key: CryptoKey, data: ArrayBuffer): Promise<ArrayBuffer>;
  decrypt(alg: Algorithm, key: CryptoKey, data: ArrayBuffer): Promise<ArrayBuffer>;
}
interface WebGlobals {
  crypto: { subtle: SubtleCrypto };
  TextEncoder: new () => { encode(s: string): Uint8Array };
  TextDecoder: new () => { decode(b: Uint8Array): string };
  btoa(s: string): string;
  atob(s: string): string;
}
const web = globalThis as unknown as WebGlobals;
const subtle = (): SubtleCrypto => web.crypto.subtle;
const enc = new web.TextEncoder();
const dec = new web.TextDecoder();
const btoa = (s: string): string => web.btoa(s);
const atob = (s: string): string => web.atob(s);

function concat(...parts: Uint8Array[]): Uint8Array {
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  let at = 0;
  for (const p of parts) {
    out.set(p, at);
    at += p.length;
  }
  return out;
}

const buf = (b: Uint8Array): ArrayBuffer => b.slice().buffer as ArrayBuffer;

export function toBase64Url(b: Uint8Array): string {
  let s = '';
  for (const x of b) s += String.fromCharCode(x);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function fromBase64Url(s: string): Uint8Array {
  if (!/^[A-Za-z0-9_-]*$/.test(s)) throw new RelayCryptoError('bad-frame', 'Not base64url.');
  const b = atob(s.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (s.length % 4)) % 4));
  return Uint8Array.from(b, (c: string) => c.charCodeAt(0));
}

async function sha256(data: Uint8Array): Promise<Uint8Array> {
  return new Uint8Array(await subtle().digest('SHA-256', buf(data)));
}

async function hmac(key: Uint8Array, data: Uint8Array): Promise<Uint8Array> {
  const k = await subtle().importKey('raw', buf(key), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
  return new Uint8Array(await subtle().sign('HMAC', k, buf(data)));
}

/** Noise HKDF with two outputs. */
async function hkdf2(ck: Uint8Array, ikm: Uint8Array): Promise<[Uint8Array, Uint8Array]> {
  const temp = await hmac(ck, ikm);
  const o1 = await hmac(temp, Uint8Array.of(1));
  const o2 = await hmac(temp, concat(o1, Uint8Array.of(2)));
  return [o1, o2];
}

function nonceBytes(n: number): Uint8Array {
  const out = new Uint8Array(12);
  new DataView(out.buffer).setBigUint64(4, BigInt(n), false);
  return out;
}

async function aead(mode: 'encrypt' | 'decrypt', key: Uint8Array, n: number, ad: Uint8Array, data: Uint8Array): Promise<Uint8Array> {
  const k = await subtle().importKey('raw', buf(key), 'AES-GCM', false, [mode]);
  const params = { name: 'AES-GCM', iv: buf(nonceBytes(n)), additionalData: buf(ad), tagLength: TAG_LEN * 8 };
  try {
    return new Uint8Array(mode === 'encrypt' ? await subtle().encrypt(params, k, buf(data)) : await subtle().decrypt(params, k, buf(data)));
  } catch {
    throw new RelayCryptoError('decrypt', 'A relayed frame failed to decrypt: it was changed in transit or sent out of order.');
  }
}

export interface RelayKeyPair {
  /** Raw 32-byte X25519 public key, base64url. This is what is registered for the org. */
  publicKey: string;
  privateKey: CryptoKey;
}

async function rawPublic(key: CryptoKey): Promise<Uint8Array> {
  return new Uint8Array(await subtle().exportKey('raw', key));
}

/** A new X25519 key pair: the hub's static relay key, or an ephemeral one. */
export async function generateRelayKeyPair(): Promise<RelayKeyPair> {
  const kp = await subtle().generateKey({ name: 'X25519' }, true, ['deriveBits']);
  return { publicKey: toBase64Url(await rawPublic(kp.publicKey)), privateKey: kp.privateKey };
}

async function dh(priv: CryptoKey, pub: Uint8Array): Promise<Uint8Array> {
  if (pub.length !== DH_LEN) throw new RelayCryptoError('bad-frame', 'A public key is not 32 bytes.');
  const key = await subtle().importKey('raw', buf(pub), { name: 'X25519' }, false, []);
  return new Uint8Array(await subtle().deriveBits({ name: 'X25519', public: key }, priv, 256));
}

/** One direction of the transport: a key and a counter that never repeats. */
class CipherState {
  private n = 0;
  private readonly k: Uint8Array;
  constructor(k: Uint8Array) {
    this.k = k;
  }
  async encrypt(plain: Uint8Array): Promise<Uint8Array> {
    if (this.n >= MAX_NONCE) throw new RelayCryptoError('closed', 'The session used up its nonces.');
    return aead('encrypt', this.k, this.n++, new Uint8Array(0), plain);
  }
  async decrypt(cipher: Uint8Array): Promise<Uint8Array> {
    const out = await aead('decrypt', this.k, this.n, new Uint8Array(0), cipher);
    this.n++;
    return out;
  }
}

class SymmetricState {
  h!: Uint8Array;
  ck!: Uint8Array;
  k: Uint8Array | null = null;
  n = 0;

  static async start(prologue: Uint8Array, responderStatic: Uint8Array): Promise<SymmetricState> {
    const s = new SymmetricState();
    const name = enc.encode(PROTOCOL_NAME);
    s.h = name.length <= 32 ? concat(name, new Uint8Array(32 - name.length)) : await sha256(name);
    s.ck = s.h;
    await s.mixHash(prologue);
    await s.mixHash(responderStatic); // NK pre-message: <- s
    return s;
  }
  async mixHash(data: Uint8Array): Promise<void> {
    this.h = await sha256(concat(this.h, data));
  }
  async mixKey(ikm: Uint8Array): Promise<void> {
    const [ck, k] = await hkdf2(this.ck, ikm);
    this.ck = ck;
    this.k = k;
    this.n = 0;
  }
  async encryptAndHash(plain: Uint8Array): Promise<Uint8Array> {
    const c = this.k ? await aead('encrypt', this.k, this.n++, this.h, plain) : plain;
    await this.mixHash(c);
    return c;
  }
  async decryptAndHash(cipher: Uint8Array): Promise<Uint8Array> {
    const p = this.k ? await aead('decrypt', this.k, this.n++, this.h, cipher) : cipher;
    await this.mixHash(cipher);
    return p;
  }
  async split(): Promise<[CipherState, CipherState]> {
    const [k1, k2] = await hkdf2(this.ck, new Uint8Array(0));
    return [new CipherState(k1), new CipherState(k2)];
  }
}

function frame(b: Uint8Array): string {
  return FRAME_PREFIX + toBase64Url(b);
}

/** Parse a relayed frame; the relay uses this too, to check shape without reading content. */
export function parseRelayFrame(text: string): Uint8Array {
  if (typeof text !== 'string' || !text.startsWith(FRAME_PREFIX)) throw new RelayCryptoError('bad-frame', 'Not an encrypted relay frame.');
  const b = fromBase64Url(text.slice(FRAME_PREFIX.length));
  if (b.length > RELAY_FRAME_MAX_BYTES) throw new RelayCryptoError('bad-frame', 'The frame is too long.');
  return b;
}

/** True when text has the shape of an encrypted frame; never decodes content. */
export function isRelayFrame(text: unknown): text is string {
  return typeof text === 'string' && text.startsWith(FRAME_PREFIX) && text.length <= FRAME_PREFIX.length + Math.ceil((RELAY_FRAME_MAX_BYTES * 4) / 3) && /^[A-Za-z0-9_-]*$/.test(text.slice(FRAME_PREFIX.length));
}

/**
 * An established session. After any failed decrypt it is closed: the counters can no longer be
 * trusted, so every later send or receive throws and the caller reports the error to the person.
 */
export class RelaySession {
  private failed: RelayCryptoError | null = null;
  private readonly send: CipherState;
  private readonly recv: CipherState;
  /** The handshake hash: both ends hold the same value, usable as a channel binding. */
  readonly handshakeHash: string;
  constructor(send: CipherState, recv: CipherState, handshakeHash: string) {
    this.send = send;
    this.recv = recv;
    this.handshakeHash = handshakeHash;
  }

  get error(): RelayCryptoError | null {
    return this.failed;
  }

  async seal(message: string): Promise<string> {
    if (this.failed) throw this.failed;
    return frame(await this.send.encrypt(enc.encode(message)));
  }

  async open(text: string): Promise<string> {
    if (this.failed) throw this.failed;
    try {
      return dec.decode(await this.recv.decrypt(parseRelayFrame(text)));
    } catch (e) {
      this.failed = e instanceof RelayCryptoError ? e : new RelayCryptoError('decrypt', 'A relayed frame failed to decrypt.');
      throw this.failed;
    }
  }
}

const PROLOGUE = enc.encode('orgus-relay-console-v1');

/**
 * Browser side. `registeredHubKey` is the hub's static public key as registered for the org; the
 * browser never takes a key from the relay. Returns the first frame to send and a function that
 * finishes on the hub's answer, refusing a hub that does not hold the registered key.
 */
export async function startRelayInitiator(registeredHubKey: string): Promise<{ hello: string; finish: (reply: string) => Promise<RelaySession> }> {
  const rs = fromBase64Url(registeredHubKey);
  if (rs.length !== DH_LEN) throw new RelayCryptoError('hub-key-mismatch', 'The registered hub key is not a 32-byte X25519 key.');
  const ss = await SymmetricState.start(PROLOGUE, rs);
  const e = await generateRelayKeyPair();
  const ePub = fromBase64Url(e.publicKey);
  // -> e, es
  await ss.mixHash(ePub);
  await ss.mixKey(await dh(e.privateKey, rs));
  const hello = frame(concat(ePub, await ss.encryptAndHash(new Uint8Array(0))));
  return {
    hello,
    finish: async (reply: string) => {
      const b = parseRelayFrame(reply);
      if (b.length < DH_LEN + TAG_LEN) throw new RelayCryptoError('hub-key-mismatch', 'The hub did not prove it holds the registered key.');
      const re = b.slice(0, DH_LEN);
      // <- e, ee
      await ss.mixHash(re);
      await ss.mixKey(await dh(e.privateKey, re));
      try {
        await ss.decryptAndHash(b.slice(DH_LEN));
      } catch {
        throw new RelayCryptoError('hub-key-mismatch', "The hub's key does not match the one registered for this org; the connection was refused.");
      }
      const [c1, c2] = await ss.split();
      return new RelaySession(c1, c2, toBase64Url(ss.h));
    },
  };
}

/** Hub side: answer a browser's first frame with the hub's static key pair. */
export async function acceptRelayInitiator(hubKey: RelayKeyPair, hello: string): Promise<{ reply: string; session: RelaySession }> {
  const ss = await SymmetricState.start(PROLOGUE, fromBase64Url(hubKey.publicKey));
  const b = parseRelayFrame(hello);
  if (b.length < DH_LEN + TAG_LEN) throw new RelayCryptoError('bad-frame', 'The handshake frame is too short.');
  const re = b.slice(0, DH_LEN);
  await ss.mixHash(re);
  await ss.mixKey(await dh(hubKey.privateKey, re));
  await ss.decryptAndHash(b.slice(DH_LEN));
  const e = await generateRelayKeyPair();
  const ePub = fromBase64Url(e.publicKey);
  await ss.mixHash(ePub);
  await ss.mixKey(await dh(e.privateKey, re));
  const reply = frame(concat(ePub, await ss.encryptAndHash(new Uint8Array(0))));
  const [c1, c2] = await ss.split();
  return { reply, session: new RelaySession(c2, c1, toBase64Url(ss.h)) };
}
